"use client";

import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

export interface CountryTabItem {
  id: string;
  label: string;
}

interface CountryStickyTabsProps {
  tabs: CountryTabItem[];
  activeTab: string;
  onTabClick: (id: string) => void;
}

export const CountryStickyTabs: React.FC<CountryStickyTabsProps> = ({
  tabs,
  activeTab,
  onTabClick,
}) => {
  const [navVisible, setNavVisible] = useState(true);
  const [lastScroll, setLastScroll] = useState(0);
  const tabsRef = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;

      if (current < 100) {
        setNavVisible(true);
      } else if (current > lastScroll) {
        // Scrolling down -> Navbar hides -> Sticky tabs go to top-0
        setNavVisible(false);
      } else {
        // Scrolling up -> Navbar appears -> Sticky tabs push down to top-[68px]/top-[72px]
        setNavVisible(true);
      }

      setLastScroll(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScroll]);

  // Auto-scroll active tab into view (especially on mobile)
  useEffect(() => {
    if (activeTab && tabsRef.current[activeTab]) {
      tabsRef.current[activeTab]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeTab]);

  return (
    <div
      className={`sticky z-[45] bg-white border-b border-gray-200 shadow-sm py-3 transition-all duration-300 ${
        navVisible ? "top-[68px] md:top-[72px]" : "top-0"
      }`}
    >
      <div className="container 2xl:max-w-[1581px] mx-auto px-3 sm:px-6">
        <div
          className="flex gap-3 overflow-x-auto scrollbar-none"
          style={{ scrollbarWidth: "none" }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                ref={(el) => {
                  tabsRef.current[tab.id] = el;
                }}
                onClick={() => onTabClick(tab.id)}
                className="relative whitespace-nowrap px-3 py-1.5 focus:outline-none ff_poppins text-base font-medium cursor-pointer"
              >
                <motion.span
                  className="relative z-10"
                  animate={{ color: isActive ? "#ffffff" : "#000000" }}
                  transition={{ duration: 0.25 }}
                >
                  {tab.label}
                </motion.span>
                {isActive && (
                  <motion.div
                    layoutId="country-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-full w-full bg-[#021A38] rounded-[12px] z-0"
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 35,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

